import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import SessionTimeout from './components/SessionTimeout';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
// Nomenclatoare / Setari
import SymptomsPage from './pages/settings/SymptomsPage';
import CausesPage from './pages/settings/CausesPage';
import InterventionTypesPage from './pages/settings/InterventionTypesPage';
import SuppliersPage from './pages/settings/SuppliersPage';
import EquipmentCategoriesPage from './pages/settings/EquipmentCategoriesPage';
import ConsumableCategoriesPage from './pages/settings/ConsumableCategoriesPage';
import ConsumablesPage from './pages/settings/ConsumablesPage';
import EquipmentPage from './pages/settings/EquipmentPage';
import BinsPage from './pages/settings/BinsPage';
// Admin
import LocationsPage from './pages/admin/LocationsPage';
import PlantsPage from './pages/admin/PlantsPage';
import GeneralSettingsPage from './pages/admin/GeneralSettingsPage';
import RolesPage from './pages/admin/RolesPage';
import UsersPage from './pages/admin/UsersPage';
import CompetenciesPage from './pages/admin/CompetenciesPage';
import WorkProceduresPage from './pages/admin/WorkProceduresPage';
import MailSettingsPage from './pages/admin/MailSettingsPage';
import BackupPage from './pages/admin/BackupPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ImportExportPage from './pages/admin/ImportExportPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import ShiftsPage from './pages/admin/ShiftsPage';
import SMSSettingsPage from './pages/admin/SMSSettingsPage';
import CompanyDataPage from './pages/admin/CompanyDataPage';
import DigitalSignaturesPage from './pages/admin/DigitalSignaturesPage';
import ScheduledNotificationsPage from './pages/admin/ScheduledNotificationsPage';
import AlertSettingsAdminPage from './pages/admin/AlertSettingsPage';
import RecycleBinPage from './pages/admin/RecycleBinPage';
import ProfilePage from './pages/admin/ProfilePage';
// Main Modules
import MaterialRequestsPage from './pages/MaterialRequestsPage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import ApprovalsPage from './pages/ApprovalsPage';
import PreventiveMaintenancePage from './pages/maintenance/PreventiveMaintenancePage';
import CorrectiveMaintenancePage from './pages/maintenance/CorrectiveMaintenancePage';
import PredictiveMaintenancePage from './pages/maintenance/PredictiveMaintenancePage';
import PMDashboardPage from './pages/maintenance/PMDashboardPage';
import PMPlanPage from './pages/maintenance/PMPlanPage';
import PMProceduresPage from './pages/maintenance/PMProceduresPage';
import BatteryServicePage from './pages/maintenance/BatteryServicePage';
import AlertsDashboardPage from './pages/maintenance/AlertsDashboardPage';
import PreventiveEntriesPage from './pages/maintenance/PreventiveEntriesPage';
import RevisionDetailPage from './pages/RevisionDetailPage';
// Reports
import ReportsPage from './pages/ReportsPage';
// Status & Calendar
import CalendarPage from './pages/CalendarPage';
import StatusPage from './pages/StatusPage';
// Other
import ScanHubPage from './pages/ScanHubPage';
import AboutPage from './pages/AboutPage';
import WMSPage from './pages/WMSPage';
import StockLocatorPage from './pages/wms/StockLocatorPage';
import ReceiptsHistoryPage from './pages/wms/ReceiptsHistoryPage';
import ReceiptsDashboardPage from './pages/wms/ReceiptsDashboardPage';
import HelpPage from './pages/HelpPage';
import QuickPickPage from './pages/QuickPickPage';
// Public (no auth)
import PublicReportPage from './pages/PublicReportPage';
// Equipment View (for technicians)
import EquipmentViewPage from './pages/EquipmentViewPage';
// Section Portal
import SectionPortalPage from './pages/SectionPortalPage';
import PortalTicketsPage from './pages/portal/PortalTicketsPage';
import PortalTicketDetailPage from './pages/portal/PortalTicketDetailPage';
import PortalEquipmentPage from './pages/portal/PortalEquipmentPage';
import PortalReportsPage from './pages/portal/PortalReportsPage';
// WMS Scanner
import ScannerPage from './pages/ScannerPage';
// WMS Additional Pages
import WMSStockPage from './pages/WMSStockPage';
import WMSInventoryPage from './pages/WMSInventoryPage';
import WMSOrdersPage from './pages/WMSOrdersPage';
import WMSLabelsPage from './pages/WMSLabelsPage';
import WMSMovementsPage from './pages/WMSMovementsPage';
import WMSActivityDashboard from './pages/wms/WMSActivityDashboard';
import BinsHeatmapPage from './pages/wms/BinsHeatmapPage';
import SmartInventoryPage from './pages/wms/SmartInventoryPage';
import StockAlertsPage from './pages/wms/StockAlertsPage';
import LeaderboardPage from './pages/wms/LeaderboardPage';
import SupplierIntelligencePage from './pages/wms/SupplierIntelligencePage';
import Cabinet3DPage from './pages/wms/Cabinet3DPage';
import StockAdjustmentPage from './pages/wms/StockAdjustmentPage';
import ArticleTraceabilityPage from './pages/wms/ArticleTraceabilityPage';
// Tools
import ToolsPage from './pages/ToolsPage';
// Shift Handover & ISCIR
import ShiftHandoverPage from './pages/ShiftHandoverPage';
import ISCIRPage from './pages/ISCIRPage';
import ISCIREquipmentPage from './pages/ISCIREquipmentPage';
import ISCIRCategoriesPage from './pages/ISCIRCategoriesPage';
import ServiceLizePage from './pages/ServiceLizePage';
// EMM
import EMMDashboardPage from './pages/EMMDashboardPage';
import EMMNomenclatorPage from './pages/EMMNomenclatorPage';
// Alerts
import AlertsPage from './pages/settings/AlertsPage';
// Analytics & Advanced
import InventoryForecastingPage from './pages/InventoryForecastingPage';
import AdvancedAnalyticsPage from './pages/AdvancedAnalyticsPage';
import EquipmentTimelinePage from './pages/EquipmentTimelinePage';
import PDFReportsPage from './pages/PDFReportsPage';
import ScheduledReportsPage from './pages/reports/ScheduledReportsPage';
import TicketConsumptionPage from './pages/reports/TicketConsumptionPage';
import AdvancedConsumptionReportPage from './pages/reports/AdvancedConsumptionReportPage';
// New Modules
import EquipmentPassportPage from './pages/EquipmentPassportPage';
import LubricationPage from './pages/LubricationPage';
import PartsConsumptionPage from './pages/PartsConsumptionPage';
import ReportBuilderPage from './pages/ReportBuilderPage';
import KioskPage from './pages/KioskPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KioskAuthorizePage from './pages/KioskAuthorizePage';
import KioskDevicesPage from './pages/admin/KioskDevicesPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import CrossDeptTasksPage from './pages/CrossDeptTasksPage';
import DocumentsLibraryPage from './pages/DocumentsLibraryPage';
import CrossDeptTaskDetailPage from './pages/CrossDeptTaskDetailPage';
import PortalCrossDeptTasksPage from './pages/portal/PortalCrossDeptTasksPage';
import PortalCrossDeptTaskDetailPage from './pages/portal/PortalCrossDeptTaskDetailPage';

// Layouts
import MainLayout from './components/layout/MainLayout';
import PortalLayout from './components/layout/PortalLayout';
import AuditLayout from './components/layout/AuditLayout';
// Audit Portal
import AuditDashboardPage from './pages/audit/AuditDashboardPage';
import AuditEquipmentDetailPage from './pages/audit/AuditEquipmentDetailPage';
import AuditScanPage from './pages/audit/AuditScanPage';
import AuditEMMPage from './pages/audit/AuditEMMPage';
// Admin - Badge Management
import BadgeManagementPage from './pages/admin/BadgeManagementPage';
// Admin - Repair Stock
import RepairStockPage from './pages/admin/RepairStockPage';

import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = window.location.pathname;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC] mx-auto"></div>
          <p className="mt-4 text-slate-600">Se incarca...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Auditor: redirect to /audit if accessing main routes
  if (user.role === 'auditor' && !location.startsWith('/audit')) {
    return <Navigate to="/audit" replace />;
  }

  // Section Chief: redirect to /portal if accessing main routes
  if (user.role === 'section_chief' && !location.startsWith('/portal')) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RealtimeProvider>
        <DataProvider>
          <Routes>
            {/* Public routes (no auth required) */}
            <Route path="/e/:code" element={<PublicReportPage />} />
            
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<AnalyticsDashboardPage />} />
              
              {/* Nomenclatoare / Setari */}
              <Route path="settings/symptoms" element={<SymptomsPage />} />
              <Route path="settings/causes" element={<CausesPage />} />
              <Route path="settings/intervention-types" element={<InterventionTypesPage />} />
              <Route path="settings/suppliers" element={<SuppliersPage />} />
              <Route path="settings/equipment-categories" element={<EquipmentCategoriesPage />} />
              <Route path="settings/consumable-categories" element={<ConsumableCategoriesPage />} />
              <Route path="settings/consumables" element={<ConsumablesPage />} />
              <Route path="settings/equipment" element={<EquipmentPage />} />
              
              {/* Admin / Configurari */}
              <Route path="admin/plants" element={<PlantsPage />} />
              <Route path="admin/locations" element={<LocationsPage />} />
              <Route path="admin/general" element={<GeneralSettingsPage />} />
              <Route path="admin/roles" element={<RolesPage />} />
              <Route path="admin/users" element={<UsersPage />} />
              <Route path="admin/competencies" element={<CompetenciesPage />} />
              <Route path="admin/procedures" element={<WorkProceduresPage />} />
              <Route path="admin/mail" element={<MailSettingsPage />} />
              <Route path="admin/alerts" element={<AlertsPage />} />
              <Route path="admin/backup" element={<BackupPage />} />
              <Route path="admin/audit-log" element={<AuditLogPage />} />
              <Route path="admin/import-export" element={<ImportExportPage />} />
              <Route path="admin/system-health" element={<SystemHealthPage />} />
              <Route path="admin/shifts" element={<ShiftsPage />} />
              <Route path="admin/sms" element={<SMSSettingsPage />} />
              <Route path="admin/company-data" element={<CompanyDataPage />} />
              <Route path="admin/signatures" element={<DigitalSignaturesPage />} />
              <Route path="admin/badges" element={<BadgeManagementPage />} />
              <Route path="admin/repair-stock" element={<RepairStockPage />} />
              <Route path="admin/scheduled-notifications" element={<ScheduledNotificationsPage />} />
              <Route path="admin/alert-settings" element={<AlertSettingsAdminPage />} />
              <Route path="admin/recycle-bin" element={<RecycleBinPage />} />
              <Route path="profile" element={<ProfilePage />} />
              
              {/* Main Modules */}
              <Route path="material-requests" element={<MaterialRequestsPage />} />
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="tickets/:id" element={<TicketDetailPage />} />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="cross-dept-tasks" element={<CrossDeptTasksPage />} />
              <Route path="cross-dept-tasks/:id" element={<CrossDeptTaskDetailPage />} />
              <Route path="documents-library" element={<DocumentsLibraryPage />} />
              <Route path="maintenance/preventive" element={<PMDashboardPage />} />
              <Route path="maintenance/preventive/entries" element={<PreventiveEntriesPage />} />
              <Route path="maintenance/preventive/plan" element={<PMPlanPage />} />
              <Route path="maintenance/preventive/procedures" element={<PMProceduresPage />} />
              <Route path="settings/pm-procedures" element={<PMProceduresPage />} />
              <Route path="maintenance/corrective" element={<CorrectiveMaintenancePage />} />
              <Route path="maintenance/predictive" element={<PredictiveMaintenancePage />} />
              <Route path="maintenance/battery-service" element={<BatteryServicePage />} />
              <Route path="maintenance/alerts-dashboard" element={<AlertsDashboardPage />} />
              <Route path="revisions/:id" element={<RevisionDetailPage />} />
              
              {/* Equipment View (for technicians via QR) */}
              <Route path="equipment/:code" element={<EquipmentViewPage />} />
              
              {/* Analytics & Advanced Features */}
              <Route path="analytics/dashboard" element={<AnalyticsDashboardPage />} />
              <Route path="analytics/advanced" element={<AdvancedAnalyticsPage />} />
              <Route path="analytics/equipment/:equipmentId/timeline" element={<EquipmentTimelinePage />} />
              <Route path="analytics/inventory-forecast" element={<InventoryForecastingPage />} />
              <Route path="reports/pdf" element={<PDFReportsPage />} />
              <Route path="reports/scheduled" element={<ScheduledReportsPage />} />
              <Route path="reports/ticket-consumption" element={<TicketConsumptionPage />} />
              <Route path="reports/advanced-consumption" element={<AdvancedConsumptionReportPage />} />
              
              {/* WMS - Separate Routes */}
              <Route path="wms" element={<WMSPage />} />
              <Route path="wms/stock" element={<WMSStockPage />} />
              <Route path="wms/stock-locator" element={<StockLocatorPage />} />
              <Route path="wms/stock-adjustment" element={<StockAdjustmentPage />} />
              <Route path="wms/traceability" element={<ArticleTraceabilityPage />} />
              <Route path="wms/receipts" element={<ReceiptsHistoryPage />} />
              <Route path="wms/receipts-dashboard" element={<ReceiptsDashboardPage />} />
              <Route path="wms/bins" element={<BinsPage />} />
              <Route path="wms/movements" element={<WMSMovementsPage />} />
              <Route path="wms/activity" element={<WMSActivityDashboard />} />
              <Route path="wms/heatmap" element={<BinsHeatmapPage />} />
              <Route path="wms/smart-inventory" element={<SmartInventoryPage />} />
              <Route path="wms/stock-alerts" element={<StockAlertsPage />} />
              <Route path="wms/leaderboard" element={<LeaderboardPage />} />
              <Route path="wms/cabinet-3d" element={<Cabinet3DPage />} />
              <Route path="wms/inventory" element={<WMSInventoryPage />} />
              <Route path="wms/orders" element={<WMSOrdersPage />} />
              <Route path="wms/labels" element={<WMSLabelsPage />} />
              <Route path="wms/scanner" element={<ScannerPage />} />
              <Route path="wms/supplier-intelligence" element={<SupplierIntelligencePage />} />
              
              {/* Scule de Lucru */}
              <Route path="tools" element={<ToolsPage />} />
              
              {/* Registru Schimb & ISCIR */}
              <Route path="shift-handover" element={<ShiftHandoverPage />} />
              <Route path="iscir" element={<ISCIRPage />} />
              <Route path="iscir/nomenclator" element={<ISCIREquipmentPage />} />
              <Route path="iscir/categorii" element={<ISCIRCategoriesPage />} />
              <Route path="iscir/service-lize" element={<ServiceLizePage />} />
              
              {/* EMM - Echipamente de Masurare si Monitorizare */}
              <Route path="emm" element={<EMMDashboardPage />} />
              <Route path="emm/nomenclator" element={<EMMNomenclatorPage />} />
              
              {/* New Modules */}
              <Route path="equipment-passport/:equipmentId" element={<EquipmentPassportPage />} />
              <Route path="lubrication" element={<LubricationPage />} />
              <Route path="parts-consumption" element={<PartsConsumptionPage />} />
              <Route path="report-builder" element={<ReportBuilderPage />} />
              <Route path="executive-dashboard" element={<ExecutiveDashboardPage />} />
              <Route path="admin/kiosk-devices" element={<KioskDevicesPage />} />
              <Route path="kiosk/authorize/:code" element={<KioskAuthorizePage />} />
              
              {/* Knowledge Base */}
              <Route path="knowledge-base" element={<KnowledgeBasePage />} />
              
              <Route path="nomenclatoare/echipamente" element={<EquipmentPage />} />
              
              {/* Reports, Calendar, Status */}
              <Route path="reports/*" element={<ReportsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="status/*" element={<StatusPage />} />
              
              {/* QR Scan */}
              <Route path="scan" element={<ScanHubPage />} />
              
              {/* Quick Pick */}
              <Route path="quick-pick" element={<QuickPickPage />} />
              <Route path="quick-pick/:revisionId" element={<QuickPickPage />} />
              
              {/* Help */}
              <Route path="help" element={<HelpPage />} />
              
              {/* About */}
              <Route path="about" element={<AboutPage />} />
            </Route>
            
            {/* Section Chief Portal - Dedicated Layout */}
            <Route path="/portal" element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }>
              <Route index element={<SectionPortalPage />} />
              <Route path="tickets" element={<PortalTicketsPage />} />
              <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
              <Route path="equipment" element={<PortalEquipmentPage />} />
              <Route path="reports" element={<PortalReportsPage />} />
              <Route path="cross-dept-tasks" element={<PortalCrossDeptTasksPage />} />
              <Route path="cross-dept-tasks/:id" element={<PortalCrossDeptTaskDetailPage />} />
            </Route>

            {/* Audit Portal - Dedicated Layout */}
            <Route path="/audit" element={
              <ProtectedRoute>
                <AuditLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AuditDashboardPage />} />
              <Route path="equipment/:code" element={<AuditEquipmentDetailPage />} />
              <Route path="equipment" element={<AuditDashboardPage />} />
              <Route path="scan" element={<AuditScanPage />} />
              <Route path="emm" element={<AuditEMMPage />} />
            </Route>

            {/* Kiosk Mode - Full Screen TV Dashboard (No auth - uses device token) */}
            <Route path="/kiosk" element={<KioskPage />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <SessionTimeout />
        </DataProvider>
        </RealtimeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
